import {
  OVRecommendActionEnum,
  ComboFrequencyEnum,
} from '../../../schema-types/globalTypes';
import validateUUID from '../../../utils/validateUUID';
import {getFeeServerFromFormik} from '../CreateBuilder/BuilderDAInitialFees';
import {
  getPotContactId,
  updateRecsVarsProps,
  upsertBuilderHeader,
  containsSingleFrequency,
  validateMutationResponse,
} from './builderHelpers';
import {getISODateOrUndefined} from '../../../utils/getISODate';

export const saveContribution = async (props: updateRecsVarsProps): Promise<string> => {
  const {accountId, destinationPots, values, builderDetails, contacts = []} = props;
  const {contribute, contact, urgency, urgencyReason} = values;
  if (!contribute) {
    throw Error('no contribute');
  }

  const leafPotId = contribute.destinationValues.existingPotId;
  const destinationPot = destinationPots?.find((x) => x.id === leafPotId);

  const ovrecommendId = await upsertBuilderHeader(props, {
    action: OVRecommendActionEnum.CONTRIBUTE,
    accountId: validateUUID(accountId),
    goalId: validateUUID(values.goal?.id),
    isNewPlanRequired: false,
    contactId: getPotContactId(contact),
    startDate:
      getISODateOrUndefined(contribute.contributeFrom?.start) ||
      getISODateOrUndefined(builderDetails?.startDate),
    productProviderId: destinationPot?.productProvider?.id,
    productNameId: destinationPot?.productName?.id,
    urgency,
    urgencyReason,
  });

  const {contributeFrom} = contribute;
  const comboFrequency = contributeFrom?.comboFrequency ?? ComboFrequencyEnum.SINGLE;

  // Employer money only comes from the employer's bank account or new cash;
  // everything else is the client's own money.
  const isEmployerSource = contributeFrom?.sourceType === 'employerBankAccount';
  const allowsEmployerAmount =
    isEmployerSource || contributeFrom?.sourceType === 'newCash';

  const singleClientAmount =
    containsSingleFrequency(comboFrequency) && !isEmployerSource
      ? Number(contributeFrom?.singleClientAmount) || 0
      : 0;
  const recurringClientAmount =
    comboFrequency !== ComboFrequencyEnum.SINGLE && !isEmployerSource
      ? Number(contributeFrom?.recurringClientAmount) || 0
      : 0;
  const singleEmployerAmount =
    containsSingleFrequency(comboFrequency) && allowsEmployerAmount
      ? Number(contributeFrom?.singleEmployerAmount) || 0
      : 0;
  const recurringEmployerAmount =
    comboFrequency !== ComboFrequencyEnum.SINGLE && allowsEmployerAmount
      ? Number(contributeFrom?.recurringEmployerAmount) || 0
      : 0;

  // DBO-1583: the Hubwise source of funds travels only with a new-money portion
  // that carries an amount; a plan source has none.
  const isNewMoneySource = Boolean(
    contributeFrom && contributeFrom.sourceType !== 'plan'
  );
  const sourceOfFunds =
    isNewMoneySource && singleClientAmount + recurringClientAmount > 0
      ? contributeFrom?.sourceOfFunds ?? null
      : null;
  const employerSourceOfFunds =
    isNewMoneySource && singleEmployerAmount + recurringEmployerAmount > 0
      ? contributeFrom?.employerSourceOfFunds ?? null
      : null;

  const contributeResult = await props.upsertBuilderContribute({
    variables: {
      ovrecommendId: validateUUID(ovrecommendId)!,
      destinationPotId: validateUUID(leafPotId)!,
      isFull:
        (contributeFrom?.sourceType === 'plan' ? contributeFrom.full : false) ?? false,
      isInSpecie:
        (contributeFrom?.sourceType === 'plan' ? contributeFrom.inSpecie : false) ??
        false,
      // The source ids alone carry the source kind: a plan moves value between
      // plans; a bank account funds new cash; an employer bank account funds
      // new cash from the employer; none of them means plain new cash.
      sourcePotId:
        contributeFrom?.sourceType === 'plan'
          ? validateUUID(contributeFrom.id)
          : undefined,
      sourceBankAccountId:
        contributeFrom?.sourceType === 'bankAccount'
          ? validateUUID(contributeFrom.id)
          : undefined,
      sourceEmployerBankAccountId:
        contributeFrom?.sourceType === 'employerBankAccount'
          ? validateUUID(contributeFrom.id)
          : undefined,
      comboFrequency,
      singleClientAmount,
      recurringClientAmount,
      singleEmployerAmount,
      recurringEmployerAmount,
      initialFeeId: validateUUID(contributeFrom?.initialFee ?? undefined),
      initialEmployerFeeId: validateUUID(contributeFrom?.initialEmployerFee ?? undefined),
      sppInitialFee: getFeeServerFromFormik(
        values.isSppDa,
        contributeFrom?.legacyInitialFee
      ),
      sppInitialEmployerFee: getFeeServerFromFormik(
        values.isSppDa,
        contributeFrom?.legacyInitialEmployerFee
      ),
      sourceOfFunds,
      employerSourceOfFunds,
    },
  });

  validateMutationResponse(
    contributeResult.data?.upsertContribute,
    'Failed to save contribution'
  );

  return ovrecommendId;
};
